/**
 * Builds scene selection groups from API item groups + CMS content.
 *
 * Availability and display group assignment come from the API;
 * upgrade labels come from the CMS `SceneSelectionPage` type.
 */
import type { ResolvedScene } from '@/components/CheckoutSummary/types'
import type { SceneGroup, SceneGroupItem } from '@/components/SceneSelection/types'
import { SceneAvailability, SceneDisplayGroup } from '@/components/SceneSelection/types'

import { CadyScene } from '@VelocitySquared/cady-sunny16-commerce-integration-models'
import type {
  CartBundleStorefrontDto,
  ItemGroupStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import { CartItemUpsertStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import type {
  SceneCatalogEntry,
  SceneSelectionPage as CmsSceneSelectionPage
} from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

import { sceneCatalogFallback } from '@/content/scene-selection/sceneSelection'

import { resolveSelectedScenes } from './resolvers'
import { getSceneAvailabilityRules } from './sceneAvailabilityRules'

// =============================================
// Constants
// =============================================

const CADY_SCENE_ID_KEY = 'CADY_SceneId'

interface SceneMapping {
  sceneId: string
  displayGroup: SceneDisplayGroup
}

/** Scenes enum value → local scene ID + display group. */
const SCENE_MAP: Record<number, SceneMapping> = {
  [CadyScene.Yearbook]: { sceneId: 'yearbook', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.CenterStage]: { sceneId: 'center-stage', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.Wonderlight]: { sceneId: 'wonderlight', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.Primetime]: { sceneId: 'prime-time', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.Shadowland]: { sceneId: 'shadowland', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.Timeless]: { sceneId: 'timeless', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.Emerge]: { sceneId: 'emerge', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.LivingWalls]: { sceneId: 'living-walls', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.Exposure]: { sceneId: 'exposure', displayGroup: SceneDisplayGroup.Studio },
  [CadyScene.Outdoor]: { sceneId: 'outdoor', displayGroup: SceneDisplayGroup.Outdoor },
  [CadyScene.City]: { sceneId: 'city', displayGroup: SceneDisplayGroup.Outdoor },
  [CadyScene.Rustic]: { sceneId: 'rustic', displayGroup: SceneDisplayGroup.Outdoor },
  [CadyScene.Beach]: { sceneId: 'beach', displayGroup: SceneDisplayGroup.Destination },
  [CadyScene.Waterfall]: { sceneId: 'waterfall', displayGroup: SceneDisplayGroup.Destination },
  [CadyScene.GoAnywhere]: {
    sceneId: 'choose-your-destination',
    displayGroup: SceneDisplayGroup.Destination
  },
  [CadyScene.Lake]: { sceneId: 'lake', displayGroup: SceneDisplayGroup.Destination }
}

/**
 * Returns the local scene ID for a given `CadyScene` enum value. Use this
 * anywhere a callsite needs to reference a specific scene by name —
 * e.g. `localSceneId(CadyScene.Yearbook)` for the auto-selected Basic/Simple
 * scene, or `localSceneId(CadyScene.GoAnywhere)` for the EU upgrade
 * auto-pick. Forces callers to think in the upstream `CadyScene` vocabulary
 * (the more stable identifier per ADR 010) and avoids per-scene named
 * constants accumulating as more scenes need to be referenced.
 */
export function localSceneId(cadyScene: CadyScene): string {
  return SCENE_MAP[cadyScene].sceneId
}

/** Reverse lookups derived from SCENE_MAP. Built in a single pass. */
const SCENE_ID_TO_DISPLAY_GROUP: Record<string, SceneDisplayGroup> = {}
const SCENE_ID_TO_CADY_ENUM: Record<string, number> = {}
for (const [enumValue, { sceneId, displayGroup }] of Object.entries(SCENE_MAP)) {
  SCENE_ID_TO_DISPLAY_GROUP[sceneId] = displayGroup
  SCENE_ID_TO_CADY_ENUM[sceneId] = Number(enumValue)
}

/**
 * Converts an array of local scene ID strings to their corresponding
 * Scenes enum values (numbers) for use in API requests.
 */
export function getCadySceneEnumValues(sceneIds: string[]): number[] {
  return sceneIds.map((id) => SCENE_ID_TO_CADY_ENUM[id]).filter((v): v is number => v !== undefined)
}

/** Returns the display group a scene belongs to, or undefined if the scene is unknown. */
export function getSceneDisplayGroup(sceneId: string): SceneDisplayGroup | undefined {
  return SCENE_ID_TO_DISPLAY_GROUP[sceneId]
}

/**
 * Returns the `CadyScene` enum value and local scene ID for an item,
 * or null if the item is not a recognized scene (no `CADY_SceneId`
 * attribute or unmapped enum value). Use the enum value to filter
 * against a school's `availableStudioSceneIds`; use the sceneId for
 * display lookups.
 */
export function getSceneRefFromAttributes(
  attributes: { key: string; value: string }[] | null | undefined
): { enumValue: number; sceneId: string } | null {
  const enumValue = getSceneIdAttribute(attributes ?? null)
  if (enumValue === null) return null
  const mapping = SCENE_MAP[enumValue]
  if (!mapping) return null
  return { enumValue, sceneId: mapping.sceneId }
}

/** Canonical display groups in render order. */
const DISPLAY_GROUPS: { id: SceneDisplayGroup; title: string }[] = [
  { id: SceneDisplayGroup.Studio, title: 'Studio Scenes*' },
  { id: SceneDisplayGroup.Outdoor, title: 'Outdoor Destinations' },
  { id: SceneDisplayGroup.Destination, title: 'Elite Unlimited Destinations' }
]

// =============================================
// Helpers
// =============================================

/**
 * Returns the `CADY_SceneId` attribute value for an item, or null
 * if the attribute is not present (non-scene item).
 */
function getSceneIdAttribute(attributes: { key: string; value: string }[] | null): number | null {
  if (!attributes) return null
  const attr = attributes.find((a) => a.key === CADY_SCENE_ID_KEY)
  if (!attr) return null
  const parsed = Number(attr.value)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Returns true if the item group contains scene items
 * (at least one item has a `CADY_SceneId` attribute).
 */
export function isSceneItemGroup(group: ItemGroupStorefrontDto): boolean {
  return (group.itemGroupItems ?? []).some(
    (igi) => getSceneIdAttribute(igi.item?.attributes ?? null) !== null
  )
}

// =============================================
// Mapper
// =============================================

interface DisplayGroupBucket {
  items: SceneGroupItem[]
  maxSelections?: number
}

/**
 * Builds scene display groups from API item groups and CMS upgrade labels.
 *
 * @param itemGroups - The bundle's `itemGroups` from the API.
 * @param pageContent - CMS page content (or fallback) — only upgrade labels are read.
 * @param availableSceneEnumIds - Optional allowlist of `CadyScene` enum values the school offers. When provided and non-empty, scenes outside this list are filtered from both API item-group items and the upgrade fallback. Empty or undefined disables filtering.
 * @returns Assembled `SceneGroup[]` with availability and upgrade labels.
 */
export function buildSceneSelectionGroups(
  itemGroups: ItemGroupStorefrontDto[],
  pageContent: CmsSceneSelectionPage,
  sessionTypeKey: string,
  availableSceneEnumIds?: readonly number[]
): SceneGroup[] {
  // 1. Initialize empty buckets for each display group
  const buckets: Record<SceneDisplayGroup, DisplayGroupBucket> = {
    [SceneDisplayGroup.Studio]: { items: [] },
    [SceneDisplayGroup.Outdoor]: { items: [] },
    [SceneDisplayGroup.Destination]: { items: [] }
  }
  const seenSceneIds = new Set<string>()
  // Empty/undefined allowlist disables filtering — guards against a backend regression shipping an empty list for a real school.
  const allowedSceneEnumIds =
    availableSceneEnumIds && availableSceneEnumIds.length > 0
      ? new Set<number>(availableSceneEnumIds)
      : null
  // 2. Process scene item groups from the API
  const sceneGroups = itemGroups.filter(isSceneItemGroup)
  for (const group of sceneGroups) {
    for (const igi of group.itemGroupItems ?? []) {
      const sceneEnumValue = getSceneIdAttribute(igi.item?.attributes ?? null)
      if (sceneEnumValue === null) continue
      const mapping = SCENE_MAP[sceneEnumValue]
      if (!mapping) continue
      if (allowedSceneEnumIds && !allowedSceneEnumIds.has(sceneEnumValue)) continue
      const { sceneId, displayGroup: displayGroupId } = mapping
      const availability: SceneAvailability = group.isIncluded
        ? SceneAvailability.Included
        : SceneAvailability.Selectable
      buckets[displayGroupId].items.push({ sceneId, availability })
      seenSceneIds.add(sceneId)
    }
  }
  // 3. Fill upgrade scenes — anything in sceneCatalog not found in any item group
  for (const scene of sceneCatalogFallback) {
    if (seenSceneIds.has(scene.identifier)) continue
    const displayGroupId = SCENE_ID_TO_DISPLAY_GROUP[scene.identifier]
    if (!displayGroupId) continue
    if (allowedSceneEnumIds) {
      const enumValue = SCENE_ID_TO_CADY_ENUM[scene.identifier]
      if (enumValue === undefined || !allowedSceneEnumIds.has(enumValue)) continue
    }
    buckets[displayGroupId].items.push({
      sceneId: scene.identifier,
      availability: SceneAvailability.Upgrade
    })
  }
  // 4. Apply business-rule availability adjustments (e.g. roadshow studio-only)
  for (const rule of getSceneAvailabilityRules(sessionTypeKey)) {
    for (const bucket of Object.values(buckets)) {
      const item = bucket.items.find((i) => i.sceneId === rule.sceneId)
      if (item) {
        item.availability = rule.availability
      }
    }
  }
  // 5. Map group IDs to their upgrade label fields
  const upgradeLabels: Record<SceneDisplayGroup, string> = {
    [SceneDisplayGroup.Studio]: pageContent.studioScenesUpgradeLabel ?? '',
    [SceneDisplayGroup.Outdoor]: pageContent.outdoorScenesUpgradeLabel ?? '',
    [SceneDisplayGroup.Destination]: pageContent.destinationScenesUpgradeLabel ?? ''
  }
  // 6. Assemble SceneGroup array with upgrade labels
  const groups: SceneGroup[] = DISPLAY_GROUPS.map(({ id, title }) => {
    const bucket = buckets[id]
    const group: SceneGroup = {
      id,
      title,
      items: bucket.items
    }
    if (bucket.maxSelections !== undefined) {
      group.maxSelections = bucket.maxSelections
    }
    if (upgradeLabels[id]) {
      group.upgradeActionLabel = upgradeLabels[id]
    }
    return group
  })
  return groups
}

/**
 * Build `CartItemUpsertStorefrontDto[]` for every scene that belongs on the
 * bundle in the cart. Emits two kinds of cart items:
 *   - User-selected scenes: an item from a non-included group whose scene ID
 *     is in `selectedSceneIds`.
 *   - Auto-included scenes: every item in any group with `isIncluded=true`.
 *
 * The backend does not auto-materialize `Included` items, so callers must
 * write them into the cart explicitly — emitting them here keeps every
 * caller (and every cart write path) consistent.
 *
 * @param selectedSceneIds - Local scene IDs the user picked (e.g. 'center-stage').
 * @param itemGroups - The session bundle's item groups from the API catalog.
 * @returns Cart item DTOs for the bundle's scenes. Items without a recognized
 *          `CADY_SceneId` mapping are silently skipped.
 */
export function buildBundleSceneItems(
  selectedSceneIds: string[],
  itemGroups: ItemGroupStorefrontDto[]
): CartItemUpsertStorefrontDto[] {
  if (itemGroups.length === 0) return []
  const selectedSet = new Set(selectedSceneIds)
  const result: CartItemUpsertStorefrontDto[] = []
  const sceneGroups = itemGroups.filter(isSceneItemGroup)
  for (const group of sceneGroups) {
    for (const igi of group.itemGroupItems ?? []) {
      const sceneEnumValue = getSceneIdAttribute(igi.item?.attributes ?? null)
      if (sceneEnumValue === null) continue
      const mapping = SCENE_MAP[sceneEnumValue]
      if (!mapping) continue
      if (!group.isIncluded && !selectedSet.has(mapping.sceneId)) continue
      const dto = new CartItemUpsertStorefrontDto()
      dto.itemGroupItemId = igi.itemGroupItemId
      dto.itemId = igi.itemId
      result.push(dto)
    }
  }
  return result
}

export function resolveCartBundleScenes(
  sessionCartBundle: CartBundleStorefrontDto,
  sceneGroups: SceneGroup[] | undefined,
  sceneCatalog: SceneCatalogEntry[]
): ResolvedScene[] {
  const sceneIds: string[] = []
  for (const cartItem of sessionCartBundle.cartItems) {
    const sceneEnumValue = getSceneIdAttribute(cartItem.item?.attributes ?? null)
    if (sceneEnumValue === null) continue
    const mapping = SCENE_MAP[sceneEnumValue]
    if (mapping) sceneIds.push(mapping.sceneId)
  }
  return resolveSelectedScenes(sceneGroups, sceneIds, sceneCatalog)
}
